import type { StatId } from '@collector/sportsapi-client-legacy'
import type {
  Participant,
  SportStats,
  Stat,
  SubParticipant,
} from '@collector/sportsapi-types'

import type {
  SportsStatConfig,
  SportsStatIdConfig,
} from '../interfaces/SportStatConfig'

export function getStatById(
  sportStatId: StatId,
  participant: Participant,
): Stat {
  return (
    participant.stats.find((s) => s.id === sportStatId) ?? {
      id: sportStatId,
      value: '',
    }
  )
}

export function statExists(
  sportStatId: StatId,
  participant: Participant | SubParticipant,
): boolean {
  if (!participant.stats) {
    return false
  }
  return !!participant.stats.find((s) => s.id === sportStatId)
}

export function getTeamStatConfigs(
  configs: SportsStatIdConfig[],
  source: SportStats[],
  homeParticipant: Participant,
  awayParticipant: Participant,
  excludeAbsent = true,
): SportsStatConfig[] {
  return configs
    .map((idConfig) => {
      const sportStat = source.find((s) => s.id === idConfig.sportStatId)
      return sportStat ? { sportStat, ...idConfig } : null
    })
    .filter((s) => !!s)
    .filter(
      (s) =>
        !excludeAbsent ||
        (statExists(s.sportStat.id, homeParticipant) &&
          statExists(s.sportStat.id, awayParticipant)),
    )
}

export function getPlayerStatConfigs(
  configs: SportsStatIdConfig[],
  source: SportStats[],
  homeSubParticipants: SubParticipant[],
  awaySubParticipants: SubParticipant[],
  excludeAbsent = true,
): SportsStatConfig[] {
  const hasSubParticipantStat = (statId: StatId): boolean =>
    homeSubParticipants.some((subParticipant) =>
      statExists(statId, subParticipant),
    ) &&
    awaySubParticipants.some((subParticipant) =>
      statExists(statId, subParticipant),
    )

  return configs.reduce<SportsStatConfig[]>((acc, idConfig) => {
    const sportStat = source.find((s) => s.id === idConfig.sportStatId)
    if (!sportStat) return acc
    const config = { sportStat, ...idConfig }
    if (!excludeAbsent || hasSubParticipantStat(sportStat.id)) {
      acc.push(config)
    }
    return acc
  }, [])
}

export function compareStats(a: Stat, b: Stat, sportStat: SportStats): number {
  const aValue = a.value ?? ''
  const bValue = b.value ?? ''

  if (sportStat.data_type === 'binary') {
    if ((+aValue === 1 && +bValue === 1) || (+aValue !== 1 && +bValue !== 1))
      return 0
    else if (+aValue === 1 && +bValue !== 1) return 1
    else return -1
  }
  if (aValue === '' && bValue === '') return 0
  else if (aValue === '') return -1
  else if (bValue === '') return 1

  const numA = typeof aValue === 'string' ? +aValue : aValue
  const numB = typeof bValue === 'string' ? +bValue : bValue
  return numA < numB ? -1 : numA > numB ? 1 : 0
}

export function isStatSame(a: Stat, b: Stat, sportStat: SportStats): boolean {
  return compareStats(a, b, sportStat) === 0
}
